import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Button, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Download as DownloadIcon, ExpandMore as ExpandMoreIcon, AutoFixHigh as AiIcon } from '@mui/icons-material';
import api from '../services/api';

interface SimplePdfViewerProps {
  courseId: number;
  latexContent?: string;
  onRetryWithAi?: (latexContent: string, errorLogs: string) => void;
  onPdfGenerated?: () => void;
}

const SimplePdfViewer: React.FC<SimplePdfViewerProps> = ({ courseId, latexContent, onRetryWithAi, onPdfGenerated }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [compilationLogs, setCompilationLogs] = useState<string | null>(null);

  useEffect(() => {
    // Debounce PDF generation when latexContent changes
    const timeoutId = setTimeout(() => {
      loadPdf();
    }, 2000); // Wait 2 seconds after last change before regenerating

    return () => clearTimeout(timeoutId);
  }, [courseId, latexContent]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPdf = async () => {
    try {
      setLoading(true);
      setError(null);
      setDebugInfo(null);
      setCompilationLogs(null);

      console.log('SimplePdfViewer: Loading PDF for course:', courseId);

      // Use axios API client for proper CORS handling
      const endpoint = latexContent
        ? `courses/${courseId}/preview_pdf_with_content/`
        : `courses/${courseId}/preview_pdf/`;

      console.log('Fetching from:', endpoint);

      const response = await api({
        url: endpoint,
        method: latexContent ? 'POST' : 'GET',
        responseType: 'blob',
        ...(latexContent && { data: { latex_content: latexContent } })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const blob = response.data;

      console.log('Blob info:', {
        size: blob.size,
        type: blob.type
      });

      // Read first bytes
      const arrayBuffer = await blob.slice(0, 20).arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
      const text = Array.from(bytes).map(b => b >= 32 && b < 127 ? String.fromCharCode(b) : '.').join('');

      console.log('First 20 bytes (hex):', hex);
      console.log('First 20 bytes (text):', text);

      setDebugInfo({
        size: blob.size,
        type: blob.type,
        firstBytesHex: hex,
        firstBytesText: text,
        isPDF: text.startsWith('%PDF')
      });

      // Create object URL
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);

      // Call callback if provided
      if (onPdfGenerated) {
        onPdfGenerated();
      }

    } catch (err: any) {
      console.error('SimplePdfViewer error:', err);

      // Try to extract compilation logs from error response
      let logs = null;
      let errorMessage = 'Erreur lors du chargement du PDF';

      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorData.detail || errorMessage;

          // Extract logs from various possible keys
          logs = errorData.latex_log || errorData.logs || errorData.compilation_logs || null;

          // If we have error_context, format it nicely
          if (errorData.error_context && Array.isArray(errorData.error_context)) {
            const contextFormatted = errorData.error_context.join('\n\n---\n\n');
            logs = logs ? `${logs}\n\n=== ERREURS DÉTECTÉES ===\n\n${contextFormatted}` : contextFormatted;
          }
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }
      } else if (err.response?.data) {
        errorMessage = err.response.data.error || err.response.data.detail || errorMessage;

        // Extract logs from various possible keys
        logs = err.response.data.latex_log || err.response.data.logs || err.response.data.compilation_logs || null;

        // If we have error_context, format it nicely
        if (err.response.data.error_context && Array.isArray(err.response.data.error_context)) {
          const contextFormatted = err.response.data.error_context.join('\n\n---\n\n');
          logs = logs ? `${logs}\n\n=== ERREURS DÉTECTÉES ===\n\n${contextFormatted}` : contextFormatted;
        }
      }

      setError(errorMessage);
      setCompilationLogs(logs);
      setDebugInfo({ error: errorMessage, logs, hasLogs: !!logs });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `course_${courseId}.pdf`;
      link.click();
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={loadPdf}>
                Réessayer
              </Button>
              {onRetryWithAi && latexContent && compilationLogs && (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<AiIcon />}
                  onClick={() => onRetryWithAi(latexContent, compilationLogs)}
                >
                  Ré-essayer avec l'IA
                </Button>
              )}
            </Box>
          }
        >
          {error}
        </Alert>

        {compilationLogs && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Logs de compilation LaTeX</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {compilationLogs}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {debugInfo && !compilationLogs && (
          <Box mt={2} p={2} bgcolor="grey.100">
            <Typography variant="caption" component="div" gutterBottom>
              Debug Info:
            </Typography>
            <pre style={{ fontSize: '0.75rem', overflow: 'auto' }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box height="100%" minHeight="600px" display="flex" flexDirection="column">
      <Box position="relative" flex={1} display="flex" flexDirection="column">
        <Box position="absolute" top={8} right={8} zIndex={1}>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            size="small"
            onClick={handleDownload}
          >
            Télécharger PDF
          </Button>
        </Box>
        {pdfUrl && (
          <embed
            src={pdfUrl}
            type="application/pdf"
            width="100%"
            style={{ border: 'none', flex: 1, minHeight: '600px' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default SimplePdfViewer;